import type { ProfileTransaction } from '@breakoutlearning/firebase-repository/stores/UserProfileTokens'
import { Spinner } from 'components/Spinner'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'

export const ProfileTransactions = observer(function ProfileTransactions() {
  const { tt } = useTranslationTyped()
  const breakoutUser = useBreakoutUser()

  const { transactions, sectionPassRebateAvailableCents } = breakoutUser
  const mappedSlideDecksToConsumedTokens =
    breakoutUser.mappedSlideDecksToConsumedTokens
  const sectionNames = breakoutUser.transactionSectionNames

  const formatDollars = (cents: number) =>
    tt.student_library.dollars({ dollars: (cents / 100).toFixed(2) })

  const formatDate = (date: Date | undefined) =>
    // Ex. 28 Jul 2024
    date ? DateTime.fromJSDate(date).toFormat('dd LLL yyyy') : ''

  const hasAssignmentRows = transactions.some(
    (transaction) => transaction.kind === 'assignmentPurchase'
  )
  const loading =
    (hasAssignmentRows &&
      typeof mappedSlideDecksToConsumedTokens === 'undefined') ||
    breakoutUser.transactionSectionNamesLoading

  const sectionNameFor = (sectionId: string | undefined) =>
    sectionId ? (sectionNames.get(sectionId) ?? undefined) : undefined

  const renderRow = (transaction: ProfileTransaction) => {
    switch (transaction.kind) {
      case 'assignmentPurchase': {
        const slideDeck = mappedSlideDecksToConsumedTokens?.[transaction.id]
        const sectionName = sectionNameFor(transaction.sectionId)
        return (
          <TransactionRow
            testId="transaction-assignment"
            imageUrl={slideDeck?.data.slideDeckImageURL}
            title={
              slideDeck?.data.slideDeckName ??
              tt.profile.assignment_purchase_fallback()
            }
            subtitle={sectionName}
            date={formatDate(transaction.date)}
            amount={String(transaction.token.data.consumedCount ?? 0)}
            amountLabel={tt.profile.tokens()}
          />
        )
      }
      case 'sectionPassPurchase': {
        const sectionName = sectionNameFor(transaction.sectionId)
        // when the section is unreadable the title already falls back to the
        // "Section Pass" label, so only add a subtitle when it says something
        // the title doesn't (the credit note, or the label under a section name)
        const subtitle = transaction.creditAppliedCents
          ? `${tt.profile.section_pass()} · ${tt.profile.credit_applied({
              dollars: formatDollars(transaction.creditAppliedCents),
            })}`
          : sectionName
            ? tt.profile.section_pass()
            : undefined
        return (
          <TransactionRow
            testId="transaction-section-pass"
            title={sectionName ?? tt.profile.section_pass()}
            subtitle={subtitle}
            date={formatDate(transaction.date)}
            amount={formatDollars(transaction.paidCents)}
          />
        )
      }
      case 'creditIssued': {
        const sectionName = sectionNameFor(transaction.rebatedFromSectionId)
        return (
          <TransactionRow
            testId="transaction-credit"
            title={tt.profile.section_pass_credit()}
            subtitle={
              sectionName
                ? tt.profile.credit_from_section({ sectionName })
                : undefined
            }
            date={formatDate(transaction.date)}
            amount={`+${formatDollars(transaction.amountCents)}`}
            amountClassName="text-core-success"
          />
        )
      }
    }
  }

  return (
    <div>
      <h2 className="text-headline-large text-core-on-tertiary">
        {tt.profile.transactions()}
      </h2>
      <p className="mt-1 text-body-large">
        {tt.profile.transactions_description()}
      </p>
      {sectionPassRebateAvailableCents > 0 && (
        <div
          data-testid="credit-available-chip"
          className="mt-3 inline-flex items-center rounded-2xl bg-surface px-4 py-2 text-label-medium text-core-on-tertiary"
        >
          {tt.profile.section_pass_credit_available()}
          <strong className="ml-1 text-title-medium">
            {formatDollars(sectionPassRebateAvailableCents)}
          </strong>
        </div>
      )}

      <ul
        data-testid="transactions-list"
        className="mt-6 flex list-none flex-col gap-y-2"
      >
        {loading && <Spinner size={3} />}
        {!loading &&
          transactions.map((transaction) => (
            <li key={`${transaction.kind}-${transaction.id}`}>
              {renderRow(transaction)}
            </li>
          ))}
      </ul>
    </div>
  )
})

const TransactionRow = observer(function TransactionRow({
  testId,
  imageUrl,
  title,
  subtitle,
  date,
  amount,
  amountLabel,
  amountClassName,
}: {
  testId: string
  imageUrl?: string
  title: string
  subtitle?: string
  date: string
  amount: string
  amountLabel?: string
  amountClassName?: string
}) {
  const { tt } = useTranslationTyped()
  return (
    <div
      role="group"
      aria-label={tt.profile.transaction()}
      data-testid={testId}
      className="flex h-[75px] w-full items-center justify-between rounded-2xl bg-surface px-5"
    >
      <div className="flex items-center">
        {imageUrl && (
          <div className="shrink-0 p-2.5">
            <img alt="" src={imageUrl} className="h-5 w-5" />
          </div>
        )}
        <div className="overflow-hidden">
          <h3 className="max-w-[150px] truncate overflow-hidden text-label-large text-core-on-tertiary lg:max-w-[300px]">
            {title}
          </h3>
          {subtitle && (
            <div className="max-w-[150px] truncate overflow-hidden text-body-medium text-on-surface-var lg:max-w-[300px]">
              {subtitle}
            </div>
          )}
          <div className="max-w-[150px] truncate overflow-hidden text-body-medium text-on-surface-var lg:max-w-[300px]">
            {date}
          </div>
        </div>
      </div>
      <div className="text-center">
        <div
          className={`text-title-large ${amountClassName ?? 'text-core-on-tertiary'}`}
        >
          {amount}
        </div>
        {amountLabel && (
          <div className="text-body-small text-on-surface-var">
            {amountLabel}
          </div>
        )}
      </div>
    </div>
  )
})
